import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { ChevronDown, Phone } from 'lucide-react';
interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
}
const faqs: FAQItem[] = [{
  question: 'How long does a typical bathroom remodel take?',
  answer: <div className="space-y-2" data-id="element-339">
        <p data-id="element-340">The timeline depends on the scope of your project:</p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-341">
          <li data-id="element-342">
            A full bathroom remodel can take 4 to 6 weeks depending on various
            factors specific to your situation.
          </li>
          <li data-id="element-343">
            A tub-to-shower conversion can take as little as 2 days for our
            basic acrylic system.
          </li>
        </ul>
      </div>
}, {
  question: 'Do you offer tub-to-shower or shower replacement options?',
  answer: 'Yes, we offer several options varying by price range. Our starter packages involve replacing with acrylic panels. We go up from there to cultured stone panels, and finally into a full tile system. All of our options involve higher than standard waterproofing, typical to what you find in full tile setups.'
}, {
  question: 'What types of bathroom remodels do you specialize in?',
  answer: "We offer a full range of bathroom remodels, including complete gut and replacement projects. We can also add a bathroom where there wasn't one previously, or expand or convert a half bath into a full bathroom. This depends on availability of plumbing and space. The price varies by complexity."
}, {
  question: 'Can you remodel my bathroom without a full gut renovation?',
  answer: 'Yes, we can replace flooring, update fixtures, replace the vanity, and replace the shower with as little full tear out as possible. We generally only need to tear out the shower bay itself to access plumbing and update it.'
}, {
  question: 'What areas of Northern Indiana do you serve?',
  answer: 'We serve all areas within an hour drive of Elkhart/South Bend. Some exceptions can be made. We are currently not located in Michigan and therefore unable to apply for contractor licensing in MI until we can afford to purchase a location there.'
}, {
  question: 'Do you handle permits and inspections if needed?',
  answer: "Yes. Generally, shower replacement doesn't require a permit. Some counties vary, but the majority do not require it for updates or replacing the shower. Major changes to plumbing generally require a plumber in Indiana, though replacement of fixtures doesn't apply to this depending on county. This is something we can work with you to subcontract out as we are not licensed plumbing contractors. Plumbing contractors generally do not offer the services we provide, so we work hand in hand with plumbing contractors to give you the best results."
}, {
  question: 'What materials do you use for showers and wall systems?',
  answer: 'Our waterproofing consists of GoBoard or equivalent waterproofing systems for tile preparations. These offer a full waterproofing solution that exceeds the requirements for acrylic showers. Our shower systems use a high-quality acrylic plastic that is glued with a strong polyurethane adhesive. We then seal everything with a quality color-matched silicone.'
}, {
  question: 'Is your remodeling process disruptive to daily use of the home?',
  answer: 'We try to be as minimally invasive as possible. We will provide a lock box for the door and you can leave a key. We do our best to be fast and clean, but all remodeling has some elements of inconvenience and we do our best to mitigate this.'
}, {
  question: 'Do you provide free consultations or estimates?',
  answer: 'Yes, we provide free consultations and estimates. If the distance is outside our normal service area, we may charge a $75 trip fee.'
}, {
  question: 'Are your remodels customized or based on standard designs?',
  answer: 'We can design you a bathroom with 3D rendering, or we can discuss basic design updates with you in person. Every project is tailored to your specific needs and preferences.'
}, {
  question: 'Do you offer accessibility or aging-in-place bathroom options?',
  answer: 'Yes, one of our most common installations is updating a shower to make it accessible for handicapped or elderly clients. We find that grab handles, shower seats, and a low-profile shower pan all make showers much more accessible and safer to use.'
}, {
  question: 'How do you protect my home during the remodeling process?',
  answer: 'We install flooring protection and site isolation with plastic and cardboard as necessary. Even with the most careful preparation, some dust or mess can happen, but we do our best to minimize the impact on your home.'
}, {
  question: 'What kind of warranty do you offer on your work and materials?',
  answer: 'We offer a 1-year warranty on parts and materials under normal use.'
}];
function FAQItem({
  item,
  isOpen,
  onToggle
}: {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-200 last:border-b-0" data-id="element-344">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-5 text-left hover:text-[#8BA888] transition-colors" data-id="element-345">
        <span className="text-lg font-medium text-gray-900 pr-4" data-id="element-346">
          {item.question}
        </span>
        <ChevronDown className={`h-5 w-5 text-[#8BA888] shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-347" />
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`} data-id="element-348">
        <div className="text-gray-600 leading-relaxed" data-id="element-349">{item.answer}</div>
      </div>
    </div>;
}
export function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <div className="flex flex-col min-h-screen pt-24 pb-20" data-id="element-350">
      {/* Hero */}
      <section className="bg-gray-50 py-16 md:py-20" data-id="element-351">
        <div className="container mx-auto px-4 md:px-6" data-id="element-352">
          <div className="max-w-3xl mx-auto text-center" data-id="element-353">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-354">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-355">
              Find answers to common questions about our bathroom remodeling
              services.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16" data-id="element-356">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl" data-id="element-357">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8" data-id="element-358">
            {faqs.map((faq, index) => <FAQItem key={index} item={faq} isOpen={openIndex === index} onToggle={() => handleToggle(index)} data-id="element-359" />)}
          </div>

          {/* CTA Section */}
          <div className="mt-12 text-center bg-[#8BA888]/10 rounded-2xl p-8" data-id="element-360">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-361">
              Still Have Questions?
            </h2>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto" data-id="element-362">
              We're here to help! Contact us for a free consultation and we'll
              answer all your questions about your bathroom remodeling project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-363">
              <Link to="/contact" data-id="element-364">
                <Button size="lg" data-id="element-365">Get Free Consultation</Button>
              </Link>
              <Button variant="outline" size="lg" data-id="element-366">
                <Phone className="mr-2 h-5 w-5" data-id="element-367" />
                (574) 914-1522
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>;
}